/**
 * `marketplace.gallery` — every user-facing string on the consumer marketplace surfaces.
 *
 * Two reasons this is a catalogue rather than literals in JSX. The first is AGM-124, as
 * amended by PRD-27 D-2: the pt-BR sibling is one directory over and declares itself
 * `: Messages['marketplace']['gallery']`, so a missing key is a compile error rather than a
 * blank on a screen. The second is AGM-022, which is the load-bearing one — the vocabulary
 * banned from consumer copy (the mechanism words listed in PRD-18's two-plane table) is
 * lintable in CI only if every consumer string is reachable from one place. A literal buried
 * three components deep is invisible to that check, and the words themselves are deliberately
 * not repeated here so a grep-shaped lint does not trip over its own rule.
 *
 * The copy rule underneath: describe outcomes, never mechanisms. "It will say it doesn't
 * know rather than guess" is what a grounded agent DOES; "answerMode: grounded" is how the
 * machine spells it, and the consumer never needs that word to make a decision.
 *
 * No `as const` here, and that is not an oversight (PRD-27 F-4): `as const` narrows every
 * value to a literal type, and the Portuguese sibling would then be typed into saying the
 * English words. The `satisfies` guards that carry real information — the lifecycle map
 * below — are kept, because those check exhaustiveness rather than freeze prose.
 */

import type { ListingState, OwnerView } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { I18n } from '@/i18n/I18nProvider.tsx'

/**
 * What a counted entry needs from the active locale (PRD-27 LOC-020).
 *
 * The locale is threaded in rather than captured, because a catalogue entry that reads a
 * module-scope locale freezes the language it was first rendered in — silently, with no type
 * error and no failing test (F-3). `Pick` rather than the whole `I18n` so the signature states
 * exactly what the entry may use — the same discipline `memory-grouping.ts` applies when it
 * takes its two group labels as an argument rather than reaching for a catalogue.
 */
type Counted = Pick<I18n, 'locale' | 'formatCount'>

export const gallery = {
  gallery: {
    title: 'Agents',
    subtitle: 'Assistants shared with you. Each one says up front what it knows and what it can do.',
    searchLabel: 'Search agents',
    searchPlaceholder: 'Search by name or what it does…',
    categoryLabel: 'Category',
    categoryAll: 'All categories',
    newAgent: 'New agent',
    /** The maker's own shelf. Named for what it holds, and matching that page's heading. */
    mine: 'Your agents',
    studio: 'Advanced',
    sections: {
      mine: 'Yours',
      featured: 'Featured',
      /** PRD-26 D-33: groups control who can use an agent, and this rail is where that shows. */
      sharedWithYourGroups: 'Shared with your groups',
      recent: 'Recently used',
      all: 'All agents'
    },
    empty: {
      title: 'No agents shared with you yet',
      body: 'When someone publishes an agent to you, it appears here. You can also build your own.'
    },
    emptySearch: {
      title: 'Nothing matched that',
      body: 'Try a shorter search, or clear the category filter to look across everything shared with you.'
    },
    failed: {
      title: 'The agent list could not be loaded',
      body: 'This is usually temporary. Reload the page, and let an administrator know if it keeps happening.'
    },
    clearFilters: 'Clear filters',
    loading: 'Loading agents…'
  },

  card: {
    officialLabel: 'Official',
    officialTitle: 'Published by this company and confirmed by an administrator.',
    ownerLine: (name: string): string => `by ${name}`,
    /**
     * `pluralCategory` rather than `count === 1`: Brazilian Portuguese puts ZERO in the
     * singular category, so a two-branch comparison is already wrong in one of the two
     * shipped languages — on the empty state of every count in the product (D-19).
     */
    knowledgeBadge: (fmt: Counted, count: number): string =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `Knowledge · ${fmt.formatCount(count)} base`
        : `Knowledge · ${fmt.formatCount(count)} bases`,
    actionsBadge: 'Actions — always asks first',
    memoryBadge: 'Remembers preferences',
    stateDraft: 'Draft — only you can see this',
    stateArchived: 'Archived',
    stateSuspended: 'Paused by an administrator',
    needsAttention: 'Needs your attention',
    needsAttentionHint: 'Open it to see what to fix.'
  },

  about: {
    back: 'All agents',
    startChat: 'Start chat',
    starting: 'Starting…',
    startFailed: 'The chat could not be started. Try again in a moment.',
    startersTitle: 'Try asking',
    starterHint: 'Picking one fills in the message so you can edit it before sending.',
    saveCopy: 'Save a copy',
    copying: 'Copying…',
    copyFailed: 'The copy could not be made. Try again in a moment.',
    copyDoneTitle: 'Your copy is ready',
    copyDoneBody: 'It starts as a draft that only you can see, so you can change it before sharing it.',
    copyDroppedTitle: 'Some things could not come with the copy',
    copyDroppedBody: 'You can only copy what you are already allowed to use. These were left out:',
    copyDroppedKnowledge: (name: string): string => `${name} — knowledge that is not shared with you`,
    copyDroppedCapability: (name: string): string => `${name} — an ability you are not able to use`,
    openCopy: 'Open your copy',
    ownerLabel: 'Owned by',
    audienceLabel: 'Who can use it',
    updatedLabel: 'Updated',
    /** A private label names the people (D-37): "Only Ana Souza", "Only Finance EMEA’s managers". */
    audiencePrivate: (owner: OwnerView): string =>
      owner.kind === 'user' ? `Only ${owner.name}` : `Only ${owner.group.name}’s managers`,
    audienceEveryone: 'Everyone who signs in',
    /**
     * A groups audience with no groups in it. There is no written-out sibling any more: the
     * only surface that renders a non-empty groups audience draws the groups as chips
     * (`AudienceLabel`), so the hand-rolled join that used to live here joined nothing and
     * hid three nouns from extraction (F-12, D-27).
     */
    audienceNobody: 'Nobody yet',
    /** Between the last two groups when they are drawn as chips rather than written out. */
    audienceAnd: 'and',
    whatsNewTitle: "What's new",
    notFound: {
      title: 'This agent is not available',
      body: 'It may have been removed, or it may not be shared with you. Check the link, or browse the agents you can use.'
    },
    loading: 'Loading…',
    suspendedTitle: 'This agent is paused',
    suspendedBody: 'An administrator paused it. You cannot start a new chat with it right now.',
    archivedTitle: 'This agent has been retired',
    archivedBody: 'Existing chats stay readable, but new ones cannot be started.',
    draftTitle: 'This agent is still a draft',
    draftBody: 'Only the people who maintain it and its co-editors can see it until it is turned on.',
    replacementLink: 'Use the replacement instead',
    /**
     * The way into the detail sheet (DTL-001), which commits to what the reader will get.
     * "Edit agent" for somebody who may change it, "View details" for somebody who may not —
     * because a control named for a thing it will not let you do is worse than no control.
     */
    editAgent: 'Edit agent',
    viewDetails: 'View details'
  },

  /**
   * The detail sheet, as read by somebody the agent is shared with (PRD-22).
   *
   * Everything on that page is already published to this reader by the listing above it; the
   * sheet lays it out the way the people who maintain the agent see it. The one thing it does
   * not carry is the instructions, and `instructionsWithheld` is the sentence that says so —
   * plainly, without pretending they do not exist and without summarising them, because a
   * machine summary of maker prose on a page about honesty is a claim nobody can check.
   */
  details: {
    title: 'Details',
    readOnly: 'You can see how this agent is set up. The people who maintain it can change it.',
    backToAgent: 'Open the agent page',
    identityTitle: 'Identity',
    identityLead: 'How this agent introduces itself.',
    nameLabel: 'Name',
    taglineLabel: 'One-line summary',
    categoryLabel: 'Category',
    descriptionLabel: 'What it is for',
    descriptionEmpty: 'No description was written for this agent.',
    instructionsTitle: 'Instructions',
    instructionsLead: 'The standing brief its maker wrote for it.',
    instructionsWithheld: 'Instructions are visible to the people who maintain this agent.',
    instructionsWithheldBody:
      'What the agent will actually do with them is below, written by the system from its real settings rather than by whoever built it.',
    startersTitle: 'Conversation starters',
    startersLead: 'Suggestions offered to anyone who opens a chat with it.',
    startersEmpty: 'No starters are offered.',
    audienceTitle: 'Who can use it',
    audienceLead: 'Who can use this agent, and who is accountable for it.',
    stateLabel: 'State',
    /**
     * The lifecycle, in a badge. Same four words the maker's own bar uses, restated here
     * rather than imported from the `marketplace.maker` namespace: this is a consumer
     * surface, and a consumer string reachable only through the maker catalogue is a string
     * that gets translated twice or not at all.
     */
    state: {
      draft: 'Draft',
      published: 'Published',
      archived: 'Retired',
      suspended: 'Paused by an administrator'
    } satisfies Record<ListingState, string>,
    notAvailable: {
      title: 'This agent is not available',
      body: 'It may have been removed, or it may not be shared with you. Browse the agents you can use.'
    },
    loadFailed: {
      title: 'This agent could not be loaded',
      body: 'This is usually temporary. Try again, and let an administrator know if it keeps happening.'
    },
    tryAgain: 'Try again',
    allAgents: 'All agents',
    /** The gallery card's accelerator (DTL-002/003). Neutral, and it names its agent. */
    cardAction: (name: string): string => `Details for ${name}`
  },

  disclosure: {
    title: 'What this agent knows and can do',
    subtitle: 'Written by the system from the agent’s real settings, not by whoever built it.',
    knowledgeTitle: 'Knowledge',
    knowledgeModeAttached: 'Answers from these knowledge bases only.',
    knowledgeModeAll: 'Searches everything you are allowed to read.',
    knowledgeEmpty: 'No knowledge bases are attached.',
    knowledgeOwner: (name: string): string => `Owned by ${name}`,
    /**
     * This reader's coverage, per base (PRD-26 GRP-045, D-16). Three states and three sentences,
     * each about the reader: they can use it; it answers through this agent although they cannot
     * open it; or it is not available to them, and the agent answers them without it.
     */
    coverage: {
      full: 'You can use it.',
      lent: (agent: string): string => `It answers through ${agent} for you. You cannot open its documents.`,
      none: (agent: string): string => `Not available to you — ${agent} answers you without it.`
    },
    coverageFull: 'You get answers from all of this agent’s knowledge.',
    coveragePartial: 'Some of this agent’s knowledge is not available to you.',
    capabilitiesTitle: 'What it can do',
    capabilitiesEmpty: 'It only answers questions — it cannot take any action on your behalf.',
    wordingReads: 'Reads information',
    wordingActs: 'Can take actions — always asks you first',
    /** GRP-062: the system behind it is limited to groups this reader is not in. */
    notAvailable: 'Not available to you',
    notAvailableHint: 'The system behind it is limited to groups you are not in, so the agent will not use it for you.',
    groundingTitle: 'How it answers',
    groundedOn: 'Answers only from its knowledge. It will say it does not know rather than guess.',
    groundedOff: 'Answers from its knowledge and from general knowledge, and shows which parts came from documents.',
    memoryTitle: 'Preferences',
    memoryGlobal: 'Uses preferences you have saved.',
    memoryGlobalOff: 'Does not use your saved preferences.',
    memoryLearns: 'Learns new preferences here.',
    memoryLearnsOff: 'Does not learn new preferences here.'
  },

  /**
   * "Ask its managers" (PRD-26 D-17) — from the agent's page (a reader it reaches) and from the
   * builder (a maker). Asking is not getting: the base's managers decide, and every sentence
   * below says so, because a "sent" that reads like "allowed" leaves somebody waiting for answers
   * that may never come. Nobody maintaining the base routes the ask to administrators, and the
   * asker is told that too.
   */
  ask: {
    action: 'Ask its managers',
    actionNoMaintainer: 'Ask an administrator',
    title: (base: string): string => `Ask ${base}’s managers`,
    titleNoMaintainer: 'Ask an administrator',
    /** What allowing it means, for everyone — never only for the asker (D-16). */
    consequence: (agent: string, base: string): string =>
      `If they allow it, ${agent} answers from ${base} for everyone who uses it. Nobody gets to open its documents through this.`,
    noMaintainer: (base: string): string => `Nobody maintains ${base} now, so an administrator decides.`,
    noteLabel: 'A note for them — optional',
    notePlaceholder: 'Legal needs the payroll rules to answer contract questions.',
    send: 'Send',
    sending: 'Sending…',
    cancel: 'Cancel',
    sent: 'Asked. You will be told what they decide.',
    sentNoMaintainer: 'Asked. An administrator will decide, and you will be told.',
    pending: (when: string): string => `You asked ${when}. Waiting for its managers.`,
    pendingNoMaintainer: (when: string): string => `You asked ${when}. Waiting for an administrator.`,
    declined: (who: string | null, when: string): string =>
      who ? `${who} said no ${when}.` : `Its managers said no ${when}.`,
    declinedNote: (note: string): string => `“${note}”`,
    askAgainLater: 'You can ask again tomorrow.',
    askAgain: 'Ask again',
    already: 'You already asked, and nobody has answered yet.',
    outOfDate: 'This page was out of date — it already answers from this for you.',
    failed: 'That could not be sent. Try again in a moment.',
    /** Refusals that asking again will not change. */
    gone: 'This is no longer available to you, so there is nothing to ask for.',
    noLongerApplies:
      'This no longer applies — the agent may have stopped using it. Reload the page to see how it stands now.'
  },

  /**
   * The agent picker, in the two places a person chooses one (PRD-19 NAV-030).
   *
   * Both hints state the same fact from the side the reader is standing on: the agent is fixed
   * when the conversation starts. That is AGM-007, and it is the one thing somebody has to know
   * *before* they press Ask rather than after — a picker that hides it produces conversations
   * people have to abandon.
   */
  picker: {
    homeTitle: 'Who answers',
    homeHint: 'Chosen when the conversation starts, and fixed from then on.',
    homeTrigger: (agentName: string): string => `${agentName} will answer. Choose a different agent.`,
    homeCurrent: 'chosen',
    chatTitle: 'Talk to another agent',
    chatHint: (agentName: string): string =>
      `This conversation stays with ${agentName}. Picking another starts a new one.`,
    /**
     * The one-agent wording, which is the state a fresh deployment is in. It says the same
     * thing the other hint does — the pin does not move — without dangling an alternative that
     * does not exist. The gallery link at the foot of the menu is where "and if I want another
     * one?" is answered.
     */
    chatHintAlone: (agentName: string): string => `This conversation stays with ${agentName}, and cannot be moved.`,
    alone: 'It is the only one shared with you so far.',
    browse: 'Browse all agents',
    chatTrigger: (agentName: string): string => `${agentName} is answering. Start a conversation with another agent.`,
    chatCurrent: 'this conversation'
  },

  notice: {
    title: (agentName: string): string => `Before you start with ${agentName}`,
    intro: 'One thing worth knowing first.',
    actionsTitle: 'This agent can take actions',
    actionsBody:
      'It can do things outside this chat, such as writing to another system. It always asks you first, and you can say no.',
    memoryTitle: 'This agent remembers preferences',
    memoryBody:
      'Things you tell it about how you like to work can be kept and used in later chats with this agent. You can review and remove them on the Memory page.',
    acknowledge: 'Got it — continue',
    dismiss: 'Not now',
    acknowledging: 'Saving…',
    failed: 'That could not be saved. Try again.',
    beforeFirstChat: 'There is one thing to read before your first chat with this agent.'
  },

  chat: {
    aboutLink: 'About this agent',
    agentLabel: 'Answering',
    updatedNotice: 'This agent was updated since you opened this chat.',
    updatedNoticeLink: 'See what changed',
    dismissUpdate: 'Dismiss',
    openingGreeting: (firstName: string): string => `Hello, ${firstName}.`,
    openingFallback: 'Ask anything. Every answer shows the passages it used and what it cost.',
    suspendedComposer: 'This agent is paused, so new messages cannot be sent.',
    archivedComposer: 'This agent has been retired, so new messages cannot be sent.',
    /**
     * Unpublished. Only its owner and co-editors ever see this line — for anyone else the
     * listing 404s and the composer has nothing to go on, so the server's door message is
     * what they read (AGM-002: gone must not be distinguishable from never-was).
     */
    draftComposer: 'This agent is not switched on, so new messages cannot be sent. Turn it on to keep using it.',
    /**
     * The audience moved and this reader is no longer in it (PRD-26 D-21). Said as what ended —
     * their use of the agent — and what did not: the conversation, which stays theirs to read.
     */
    unavailableComposer: (agent: string | null): string =>
      `You can no longer use ${agent ?? 'this agent'}, so new messages cannot be sent. This conversation stays readable.`,
    composerPlaceholder: 'Ask a question…',
    unknownAgent: 'Assistant'
  },

  /**
   * Home.
   *
   * Four entries shorter than it was. `title`, `subtitle`, `begin` and `loading` belonged to
   * the agent-picker screen `/` used to be before the redesign, and the last of them —
   * `title`, borrowed as the old `<select>`'s accessible name — went with that control. A
   * catalog exists so a translator can find every string the product says; an entry nothing
   * renders is a sentence somebody pays to translate into a language nobody reads it in.
   */
  start: {
    browseAll: 'Browse all agents',
    failed: 'The chat could not be started. Try again in a moment.',
    empty: {
      title: 'No agents are shared with you yet',
      body: 'An administrator can share one with you, or you can build your own.'
    }
  },

  notifications: {
    open: 'Notifications',
    openWithUnread: (fmt: Counted, count: number): string =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} unread notification`
        : `${fmt.formatCount(count)} unread notifications`,
    title: 'Notifications',
    close: 'Close notifications',
    markAllRead: 'Mark all as read',
    unreadDot: 'Unread',
    empty: {
      title: 'Nothing to catch up on',
      body: 'Requests waiting for you, answers to what you asked, and news about what you maintain show up here.'
    },
    failed: 'Notifications could not be loaded.'
  }
}

/*
 * Four exports were deleted here, for the reason `start` above documents.
 *
 * `audienceLabel(audience, owner)` and `groupWithSize(group)` had no call site left: the
 * `AudienceLabel` component superseded the first — an audience is DRAWN now, each group as its
 * avatar and its name (PRD-26 GRP-102) — and nothing ever rendered the second. With them went
 * this file's private `joinNames` and `people`, the second and fourth copies of two primitives
 * that hardcoded " and " and an "en" locale argument, and hid their nouns from every extraction
 * tool (PRD-27 F-12). A dead entry is deleted rather than translated (D-27); the live list join
 * is `useI18n().formatList` and the live count is a catalogue entry owning its own plural forms.
 *
 * Those two calls are named in prose rather than written out, for the same reason the banned
 * vocabulary at the top of this file is: the discipline suite greps this source, and a comment
 * quoting a pattern trips the rule it is explaining.
 */
